// src/pages/LoginPage.js — Two-column animated login
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';

// Star/Moon canvas animation
function StarField() {
  useEffect(() => {
    const canvas = document.getElementById('star-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const stars = Array.from({length:120}, () => ({
      x: Math.random()*canvas.width, y: Math.random()*canvas.height,
      r: Math.random()*1.5+0.3, o: Math.random(), speed: Math.random()*0.005+0.002,
    }));
    const moons = Array.from({length:4}, (_, i) => ({
      x: Math.random()*canvas.width, y: Math.random()*canvas.height,
      r: Math.random()*6+4, o: Math.random()*0.3+0.1,
      dx: (Math.random()-0.5)*0.3, dy: (Math.random()-0.5)*0.2,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      stars.forEach(s => {
        s.o += s.speed; if (s.o>1) s.o=0;
        ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2);
        ctx.fillStyle = `rgba(255,255,255,${Math.abs(Math.sin(s.o))})`;
        ctx.fill();
      });
      moons.forEach(m => {
        m.x+=m.dx; m.y+=m.dy;
        if(m.x<0||m.x>canvas.width) m.dx*=-1;
        if(m.y<0||m.y>canvas.height) m.dy*=-1;
        ctx.beginPath();
        ctx.arc(m.x,m.y,m.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,215,0,${m.o})`;
        ctx.fill();
        // crescent effect
        ctx.beginPath();
        ctx.arc(m.x+m.r*0.3,m.y,m.r*0.85,0,Math.PI*2);
        ctx.fillStyle='rgba(13,0,37,0.9)';
        ctx.fill();
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, []);
  return <canvas id="star-canvas" className="absolute inset-0 w-full h-full" />;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate   = useNavigate();
  const [form, setForm]       = useState({ email:'', password:'' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const handleChange = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(form.email,form.password); navigate('/dashboard'); }
    catch(err) { setError(err.response?.data?.message||'Invalid credentials. Please try again.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex overflow-hidden" style={{background:'#0d0025'}}>
      {/* LEFT — Animated cosmic panel */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col items-center justify-center overflow-hidden"
           style={{background:'linear-gradient(135deg,#0d0025 0%,#1a0047 50%,#2a0060 100%)'}}>
        <StarField />
        {/* Content over canvas */}
        <div className="relative z-10 text-center px-12">
          <div className="text-8xl mb-6" style={{filter:'drop-shadow(0 0 30px rgba(129,87,255,0.8))'}}>
            🔮
          </div>
          <h1 className="font-cinzel text-5xl font-bold text-white mb-4 tracking-wide"
              style={{textShadow:'0 0 40px rgba(129,87,255,0.8)'}}>
            AstroVeda
          </h1>
          <p className="text-lg text-purple-200 mb-8 tracking-widest uppercase text-sm">
            Vedic Astrology Platform
          </p>
          <div className="w-24 h-0.5 mx-auto mb-8"
               style={{background:'linear-gradient(90deg,transparent,#ffd700,transparent)'}} />
          <p className="text-purple-300 text-base leading-relaxed max-w-sm mx-auto italic">
            "The stars are the jewels of the night, and perchance surpass anything which day has to show."
          </p>
          <p className="text-purple-500 text-sm mt-3">— Henry David Thoreau</p>

          {/* Floating zodiac symbols */}
          <div className="flex justify-center gap-4 mt-10 text-2xl opacity-40">
            {'♈ ♉ ♊ ♋ ♌ ♍'.split(' ').map((s,i)=>(
              <span key={i} className="animate-pulse" style={{animationDelay:`${i*0.3}s`}}>{s}</span>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-3 text-2xl opacity-40">
            {'♎ ♏ ♐ ♑ ♒ ♓'.split(' ').map((s,i)=>(
              <span key={i} className="animate-pulse" style={{animationDelay:`${i*0.3+0.15}s`}}>{s}</span>
            ))}
          </div>

          {/* Orbiting planets decoration */}
          <div className="relative mx-auto mt-8 w-32 h-32">
            <div className="absolute inset-0 rounded-full border border-purple-700/30 animate-spin-slow" />
            <div className="absolute inset-4 rounded-full border border-purple-600/20 animate-spin-slow"
                 style={{animationDirection:'reverse',animationDuration:'15s'}} />
            <div className="absolute inset-8 rounded-full border border-gold-500/20 animate-spin-slow"
                 style={{animationDuration:'8s'}} />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 text-lg">☀️</div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-base">🌙</div>
          </div>
        </div>
      </div>

      {/* RIGHT — Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-10 relative"
           style={{background:'rgba(13,0,37,0.97)'}}>
        {/* Subtle glow */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
             style={{background:'radial-gradient(circle,#6a2ff0,transparent)',filter:'blur(60px)'}} />

        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="text-5xl mb-3">🔮</div>
            <h1 className="font-cinzel text-3xl font-bold text-white">AstroVeda</h1>
          </div>

          {/* Form card */}
          <div className="rounded-2xl p-8 border border-purple-800/40"
               style={{background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',
                       boxShadow:'0 0 40px rgba(106,47,240,0.15),inset 0 1px 0 rgba(255,255,255,0.05)'}}>
            <div className="mb-7">
              <h2 className="font-cinzel text-2xl font-bold text-white mb-1">Welcome Back</h2>
              <p className="text-purple-400 text-sm">Sign in to your cosmic journey</p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm bg-red-900/30 border-red-500/40 text-red-300">
                <span className="mt-0.5">⚠</span><span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-purple-300 mb-1.5 tracking-wide uppercase text-xs">
                  Email Address
                </label>
                <input type="email" name="email" required
                  className="w-full px-4 py-3.5 rounded-xl text-purple-100 placeholder-purple-600 focus:outline-none transition-all"
                  style={{background:'rgba(106,47,240,0.1)',border:'1px solid rgba(106,47,240,0.3)'}}
                  placeholder="your@email.com"
                  value={form.email} onChange={handleChange}
                  onFocus={e=>e.target.style.borderColor='rgba(129,87,255,0.8)'}
                  onBlur={e=>e.target.style.borderColor='rgba(106,47,240,0.3)'} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-purple-300 mb-1.5 tracking-wide uppercase text-xs">
                  Password
                </label>
                <input type="password" name="password" required
                  className="w-full px-4 py-3.5 rounded-xl text-purple-100 placeholder-purple-600 focus:outline-none transition-all"
                  style={{background:'rgba(106,47,240,0.1)',border:'1px solid rgba(106,47,240,0.3)'}}
                  placeholder="Enter your password"
                  value={form.password} onChange={handleChange}
                  onFocus={e=>e.target.style.borderColor='rgba(129,87,255,0.8)'}
                  onBlur={e=>e.target.style.borderColor='rgba(106,47,240,0.3)'} />
              </div>

              <button type="submit" disabled={loading}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-300 mt-2"
                style={{
                  background:'linear-gradient(135deg,#6a2ff0,#4c12b5)',
                  boxShadow:'0 4px 20px rgba(106,47,240,0.5)',
                }}
                onMouseEnter={e=>e.target.style.boxShadow='0 8px 30px rgba(106,47,240,0.8)'}
                onMouseLeave={e=>e.target.style.boxShadow='0 4px 20px rgba(106,47,240,0.5)'}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner size="sm" /> Signing in...
                  </span>
                ) : '✨ Sign In to AstroVeda'}
              </button>
            </form>




          </div>

          {/* Bottom signs */}
          <p className="text-center text-purple-700 text-xs mt-6 tracking-widest">
            ✦ VSOP87 Real Ephemeris &bull; 22-Section Kundali &bull; Ashta Koota Matching ✦
          </p>
        </div>
      </div>
    </div>
  );
}
